import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { toast } from '../lib/toast';
import BottomNav from '../components/BottomNav';
import { ArrowLeft, Settings as SettingsIcon, Shield, Image as ImageIcon } from 'lucide-react';

export default function Settings() {
  const navigate = useNavigate();
  const { currentUser, updateUserProfile } = useEmberStore();
  const [weightUnit, setWeightUnit] = useState<'grams' | 'ounces'>('grams');
  const [saving, setSaving] = useState(false);

  // Display & accessibility
  const [highContrast, setHighContrast] = useState(false);
  
  // Notification preferences
  const [eventNotificationsEnabled, setEventNotificationsEnabled] = useState(true);
  const [eventNotificationRadius, setEventNotificationRadius] = useState(50);

  // Privacy settings (default to true if column doesn't exist yet)
  const [isProfilePublic, setIsProfilePublic] = useState(true);

  // Profile bio
  const [bio, setBio] = useState('');

  // Editable profile fields
  const [smokerType, setSmokerType] = useState<'pipe' | 'cigar' | 'both'>('pipe');
  const [pipeShapes, setPipeShapes] = useState<string[]>([]);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  const availableShapes = ['Billiard','Dublin','Apple','Bent Apple','Poker','Bulldog','Lovat','Prince','Author','Canadian'];

  useEffect(() => {
    // Initialize theme from localStorage
    if (typeof window !== 'undefined') {
      const storedTheme = window.localStorage.getItem('ember-theme');
      if (storedTheme === 'high-contrast') {
        setHighContrast(true);
        document.documentElement.setAttribute('data-theme', 'high-contrast');
      } else {
        document.documentElement.removeAttribute('data-theme');
      }
    }

    if (currentUser?.weight_unit) {
      setWeightUnit(currentUser.weight_unit);
    }
    if (currentUser?.event_notifications_enabled !== undefined) {
      setEventNotificationsEnabled(currentUser.event_notifications_enabled);
    }
    if (currentUser?.event_notification_radius_km) {
      setEventNotificationRadius(currentUser.event_notification_radius_km);
    }
    if (currentUser?.smoker_type) {
      setSmokerType(currentUser.smoker_type);
    }
    if (currentUser?.pipe_shapes) {
      setPipeShapes(currentUser.pipe_shapes);
    }
    if (currentUser?.avatar_url) {
      setAvatarPreview(currentUser.avatar_url);
    }
    // Handle case where is_profile_public column might not exist yet
    if (currentUser && 'is_profile_public' in currentUser && currentUser.is_profile_public !== undefined) {
      setIsProfilePublic(currentUser.is_profile_public);
    } else {
      setIsProfilePublic(true); // Default to public
    }
    if (currentUser?.bio) {
      setBio(currentUser.bio);
    }
  }, [currentUser]);

  async function handleWeightUnitChange(newUnit: 'grams' | 'ounces') {
    if (!currentUser) return;

    setWeightUnit(newUnit);
    setSaving(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ weight_unit: newUnit })
        .eq('id', currentUser.id);

      if (error) throw error;

      // Update local state
      updateUserProfile({ ...currentUser, weight_unit: newUnit });
    } catch (error) {
      console.error('Error updating weight unit:', error);
      toast.error('Failed to save preference');
      // Revert on error
      setWeightUnit(currentUser.weight_unit || 'grams');
    } finally {
      setSaving(false);
    }
  }

  // Location sharing feature removed; relying on profile privacy only

  async function handleEventNotificationsChange(enabled: boolean) {
    if (!currentUser) return;

    setEventNotificationsEnabled(enabled);
    setSaving(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ event_notifications_enabled: enabled })
        .eq('id', currentUser.id);

      if (error) throw error;

      // Update local state
      updateUserProfile({ ...currentUser, event_notifications_enabled: enabled });
      toast.success(enabled ? 'Event notifications enabled' : 'Event notifications disabled');
    } catch (error) {
      console.error('Error updating event notifications:', error);
      toast.error('Failed to save preference');
      // Revert on error
      setEventNotificationsEnabled(currentUser.event_notifications_enabled !== false);
    } finally {
      setSaving(false);
    }
  }

  async function handleNotificationRadiusChange(radiusKm: number) {
    if (!currentUser) return;

    // Validate range (1-500km)
    const validRadius = Math.max(1, Math.min(radiusKm, 500));
    setEventNotificationRadius(validRadius);
    setSaving(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ event_notification_radius_km: validRadius })
        .eq('id', currentUser.id);

      if (error) throw error;

      // Update local state
      updateUserProfile({ ...currentUser, event_notification_radius_km: validRadius });
    } catch (error) {
      console.error('Error updating notification radius:', error);
      toast.error('Failed to save preference');
      // Revert on error
      setEventNotificationRadius(currentUser.event_notification_radius_km || 50);
    } finally {
      setSaving(false);
    }
  }

  async function uploadAvatarIfNeeded(): Promise<string | null> {
    if (!avatarFile || !currentUser) return null;
    try {
      const filePath = `${currentUser.id}/avatar-${Date.now()}-${avatarFile.name}`;
      const { data, error } = await supabase.storage
        .from('profile-avatars')
        .upload(filePath, avatarFile, { upsert: false });
      if (error || !data) throw error;
      const { data: pub } = supabase.storage
        .from('profile-avatars')
        .getPublicUrl(data.path);
      return pub?.publicUrl ?? null;
    } catch (e) {
      console.error('Avatar upload failed:', e);
      toast.error('Failed to upload avatar');
      return null;
    }
  }

  async function saveProfileEdits() {
    if (!currentUser) return;
    setSaving(true);
    try {
      const avatar_url = await uploadAvatarIfNeeded();
      const updates: any = {
        smoker_type: smokerType,
        pipe_shapes: pipeShapes,
        bio: bio,
      };
      if (avatar_url) updates.avatar_url = avatar_url;

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', currentUser.id);
      if (error) throw error;

      updateUserProfile({ ...currentUser, ...updates });
      toast.success('Profile updated');
      if (avatar_url) {
        setAvatarPreview(avatar_url);
        setAvatarFile(null);
      }
    } catch (e) {
      console.error('Error saving profile:', e);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  }

  function toggleHighContrastMode() {
    const next = !highContrast;
    setHighContrast(next);
    if (typeof document !== 'undefined') {
      if (next) {
        document.documentElement.setAttribute('data-theme', 'high-contrast');
        window.localStorage.setItem('ember-theme', 'high-contrast');
      } else {
        document.documentElement.removeAttribute('data-theme');
        window.localStorage.setItem('ember-theme', 'default');
      }
    }
  }

  function toggleShape(shape: string) {
    setPipeShapes((prev) => prev.includes(shape) ? prev.filter(s => s !== shape) : [...prev, shape]);
  }

  function onAvatarInput(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] || null;
    setAvatarFile(f);
    if (f) {
      const r = new FileReader();
      r.onload = ev => setAvatarPreview(ev.target?.result as string);
      r.readAsDataURL(f);
    } else {
      setAvatarPreview(currentUser?.avatar_url || null);
    }
  }

  async function handleProfilePrivacyChange(isPublic: boolean) {
    if (!currentUser) return;

    setIsProfilePublic(isPublic);
    setSaving(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_profile_public: isPublic })
        .eq('id', currentUser.id);

      if (error) throw error;

      // Update local state
      updateUserProfile({ ...currentUser, is_profile_public: isPublic });
      toast.success(isPublic ? 'Profile is now public' : 'Profile is now private');
    } catch (error) {
      console.error('Error updating profile privacy:', error);
      toast.error('Failed to save privacy setting');
      // Revert on error
      const currentPrivacy = currentUser && 'is_profile_public' in currentUser ? currentUser.is_profile_public : true;
      setIsProfilePublic(currentPrivacy !== false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '80px' }}>
      {/* Header */}
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer' }}
        >
          <ArrowLeft size={24} />
        </button>
        <SettingsIcon size={24} color="var(--color-accent)" />
        <h1 style={{ fontSize: '1.5rem', fontFamily: '"Oswald", sans-serif', letterSpacing: '-0.02em' }}>
          Settings
        </h1>
      </header>

      <div className="container" style={{ paddingTop: '1rem' }}>
        {/* Edit Profile */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', marginBottom: '0.5rem', fontWeight: '600' }}>
            Edit Profile
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '1rem', alignItems: 'start' }}>
            {/* Avatar */}
            <div>
              <div style={{
                width: '100px', height: '100px', borderRadius: '50%', overflow: 'hidden',
                backgroundColor: '#1f1f1f', border: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <ImageIcon size={28} color="#666" />
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <label htmlFor="avatar-upload" style={{ padding: '0.4rem 0.6rem', border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer', backgroundColor: 'var(--color-card)', color: '#ccc', fontSize: '0.8rem' }}>Upload</label>
                {avatarPreview && (
                  <button onClick={() => { setAvatarFile(null); setAvatarPreview(null); }} style={{ padding: '0.4rem 0.6rem', border: '1px solid var(--color-danger)', borderRadius: '6px', cursor: 'pointer', backgroundColor: 'transparent', color: 'var(--color-danger)', fontSize: '0.8rem' }}>Remove</button>
                )}
              </div>
              <input id="avatar-upload" type="file" accept="image/*" onChange={onAvatarInput} style={{ display: 'none' }} />
            </div>

            {/* Fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div>
                <div style={{ marginBottom: '0.35rem', fontSize: '0.85rem', color: '#999' }}>Smoker Type</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {(['pipe','cigar','both'] as const).map((opt) => (
                    <button key={opt} onClick={() => setSmokerType(opt)} disabled={saving}
                      style={{ padding: '0.5rem 0.75rem', borderRadius: '6px', border: '2px solid', borderColor: smokerType === opt ? 'var(--color-accent)' : 'var(--color-border)', backgroundColor: smokerType === opt ? 'rgba(196,255,0,0.1)' : 'var(--color-card)', color: smokerType === opt ? 'var(--color-accent)' : 'var(--color-text)', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: smokerType === opt ? 700 : 500 }}>
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div style={{ marginBottom: '0.35rem', fontSize: '0.85rem', color: '#999' }}>Preferred Pipe Shapes</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {availableShapes.map(shape => (
                    <button key={shape} type="button" onClick={() => toggleShape(shape)} disabled={saving}
                      style={{ padding: '0.35rem 0.6rem', borderRadius: '999px', border: '1px solid', borderColor: pipeShapes.includes(shape) ? 'var(--color-accent)' : 'var(--color-border)', backgroundColor: pipeShapes.includes(shape) ? 'rgba(196,255,0,0.1)' : 'transparent', color: pipeShapes.includes(shape) ? 'var(--color-accent)' : '#ccc', fontSize: '0.8rem', cursor: saving ? 'not-allowed' : 'pointer' }}>
                      {shape}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div style={{ marginBottom: '0.35rem', fontSize: '0.85rem', color: '#999' }}>Bio</div>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell the community about yourself, your favorite tobaccos, or your smoking journey..."
                  disabled={saving}
                  maxLength={500}
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '0.75rem',
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    color: 'var(--color-text)',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
                <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem', textAlign: 'right' }}>
                  {bio.length}/500 characters
                </div>
              </div>

              <div>
                <button onClick={saveProfileEdits} disabled={saving} style={{ padding: '0.6rem 0.9rem', backgroundColor: 'var(--color-accent)', border: 'none', color: '#0b0b0b', borderRadius: '8px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Display & Accessibility */}
        <div className="card">
          <h3 style={{ fontSize: '1.125rem', marginBottom: '0.5rem', fontWeight: '600' }}>
            Display & Accessibility
          </h3>
          <p style={{ fontSize: '0.875rem', color: '#999', marginBottom: '1rem' }}>
            Adjust contrast and background to suit your eyesight and environment.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'center',
            columnGap: '0.75rem',
            padding: '0.875rem 1rem',
            backgroundColor: highContrast ? 'rgba(17, 17, 17, 0.04)' : 'var(--color-card)',
            border: `2px solid ${highContrast ? 'var(--color-border)' : 'var(--color-border)'}`,
            borderRadius: '12px',
            marginBottom: '0.75rem'
          }}>
            <div>
              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                High Contrast (Light) Theme
              </div>
              <div style={{ fontSize: '0.875rem', color: '#666' }}>
                {highContrast
                  ? 'Using a light grey background with bold dark text and muted accents.'
                  : 'Tap to switch to a light high-contrast theme with softer colors.'}
              </div>
            </div>
            <button
              onClick={toggleHighContrastMode}
              style={{
                width: '50px',
                height: '28px',
                backgroundColor: highContrast ? 'var(--color-accent)' : '#666',
                border: 'none',
                borderRadius: '14px',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: '#ffffff',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: highContrast ? '24px' : '2px',
                transition: 'left 0.2s ease'
              }} />
            </button>
          </div>
        </div>

        {/* Weight Unit Preference */}
        <div className="card">
          <h3 style={{ fontSize: '1.125rem', marginBottom: '0.5rem', fontWeight: '600' }}>
            Weight Unit Preference
          </h3>
          <p style={{ fontSize: '0.875rem', color: '#999', marginBottom: '1rem' }}>
            Choose your preferred unit for tobacco amounts
          </p>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={() => handleWeightUnitChange('grams')}
              disabled={saving}
              style={{
                flex: 1,
                padding: '1rem',
                border: '2px solid',
                borderColor: weightUnit === 'grams' ? 'var(--color-accent)' : 'var(--color-border)',
                backgroundColor: weightUnit === 'grams' ? 'rgba(196, 255, 0, 0.1)' : 'var(--color-card)',
                color: weightUnit === 'grams' ? 'var(--color-accent)' : 'var(--color-text)',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: weightUnit === 'grams' ? '600' : '400',
                fontSize: '1rem',
                transition: 'all 0.2s',
              }}
            >
              Grams (g)
              {weightUnit === 'grams' && <span style={{ display: 'block', fontSize: '0.75rem', marginTop: '0.25rem' }}>✓ Selected</span>}
            </button>

            <button
              onClick={() => handleWeightUnitChange('ounces')}
              disabled={saving}
              style={{
                flex: 1,
                padding: '1rem',
                border: '2px solid',
                borderColor: weightUnit === 'ounces' ? 'var(--color-accent)' : 'var(--color-border)',
                backgroundColor: weightUnit === 'ounces' ? 'rgba(196, 255, 0, 0.1)' : 'var(--color-card)',
                color: weightUnit === 'ounces' ? 'var(--color-accent)' : 'var(--color-text)',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: weightUnit === 'ounces' ? '600' : '400',
                fontSize: '1rem',
                transition: 'all 0.2s',
              }}
            >
              Ounces (oz)
              {weightUnit === 'ounces' && <span style={{ display: 'block', fontSize: '0.75rem', marginTop: '0.25rem' }}>✓ Selected</span>}
            </button>
          </div>

          {saving && (
            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: 'var(--color-accent)', textAlign: 'center' }}>
              Saving...
            </p>
          )}
        </div>

        {/* Location Sharing removed: now governed by Profile Privacy only */}

        {/* Username Visibility */}
        <div className="card" style={{ marginTop: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <Shield size={20} color="var(--color-accent)" />
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>
              Username Visibility
            </h3>
          </div>
          <p style={{ fontSize: '0.875rem', color: '#999', marginBottom: '1rem' }}>
            Control whether your username appears on check-in cards
          </p>
          
          <div style={{
            padding: '0.75rem',
            backgroundColor: 'rgba(255, 165, 0, 0.1)',
            border: '1px solid rgba(255, 165, 0, 0.3)',
            borderRadius: '6px',
            fontSize: '0.875rem',
            color: '#cfcfcf',
            marginBottom: '1rem'
          }}>
            <strong style={{ color: '#ffa500' }}>Note:</strong> Profile privacy requires database admin to add the column. For now, all profiles are public.
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'center',
            columnGap: '0.75rem',
            padding: '0.875rem 1rem',
            backgroundColor: isProfilePublic ? 'rgba(196, 255, 0, 0.1)' : 'var(--color-card)',
            border: `2px solid ${isProfilePublic ? 'var(--color-accent)' : 'var(--color-border)'}`,
            borderRadius: '12px',
            marginBottom: '0.75rem'
          }}>
            <div>
              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                {isProfilePublic ? 'Username Visible' : 'Username Hidden'}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#999' }}>
                {isProfilePublic ? 'Your username appears on check-in cards' : 'Your username is hidden on check-in cards'}
              </div>
            </div>
            <button
              onClick={() => handleProfilePrivacyChange(!isProfilePublic)}
              disabled={saving}
              style={{
                width: '50px',
                height: '28px',
                backgroundColor: isProfilePublic ? 'var(--color-accent)' : '#666',
                border: 'none',
                borderRadius: '14px',
                position: 'relative',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: 'white',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: isProfilePublic ? '24px' : '2px',
                transition: 'left 0.2s ease'
              }} />
            </button>
          </div>

          <div style={{
            padding: '0.75rem',
            backgroundColor: isProfilePublic ? 'rgba(196, 255, 0, 0.05)' : 'rgba(255, 68, 68, 0.1)',
            border: `1px solid ${isProfilePublic ? 'rgba(196, 255, 0, 0.2)' : 'rgba(255, 68, 68, 0.3)'}`,
            borderRadius: '6px',
            fontSize: '0.875rem',
            color: '#cfcfcf'
          }}>
            {isProfilePublic ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Shield size={16} color="var(--color-accent)" />
                  <strong>Public Profile</strong>
                </div>
                • Your profile is visible to all users<br/>
                • Anyone can send you connection requests<br/>
                • Your public check-ins are visible on the map
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Shield size={16} color="#e4574d" />
                  <strong>Private Profile</strong>
                </div>
                • Only accepted connections can view your profile<br/>
                • Your activity is hidden from non-connections<br/>
                • You can still browse and connect with others
              </>
            )}
          </div>

          {saving && (
            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: 'var(--color-accent)', textAlign: 'center' }}>
              Saving...
            </p>
          )}
        </div>

        {/* Event Notifications */}
        <div className="card" style={{ marginTop: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem' }}>🔔</span>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>
              Event Notifications
            </h3>
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text)', marginBottom: '1rem' }}>
            Get notified when events are created near your recent check-in locations
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'center',
            columnGap: '0.75rem',
            padding: '0.875rem 1rem',
            backgroundColor: eventNotificationsEnabled ? 'rgba(196, 255, 0, 0.1)' : 'var(--color-card)',
            border: `2px solid ${eventNotificationsEnabled ? 'var(--color-accent)' : 'var(--color-border)'}`,
            borderRadius: '12px',
            marginBottom: '1rem'
          }}>
            <div>
              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                Event Proximity Alerts
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text)' }}>
                {eventNotificationsEnabled ? 'You\'ll be notified of nearby events' : 'No event notifications'}
              </div>
            </div>
            <button
              onClick={() => handleEventNotificationsChange(!eventNotificationsEnabled)}
              disabled={saving}
              style={{
                width: '50px',
                height: '28px',
                backgroundColor: eventNotificationsEnabled ? 'var(--color-accent)' : '#666',
                border: 'none',
                borderRadius: '14px',
                position: 'relative',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: 'white',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: eventNotificationsEnabled ? '24px' : '2px',
                transition: 'left 0.2s ease'
              }} />
            </button>
          </div>

          {eventNotificationsEnabled && (
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '500' }}>
                Notification Radius: {eventNotificationRadius} km
              </label>
              <input
                type="range"
                min="1"
                max="500"
                step="5"
                value={eventNotificationRadius}
                onChange={(e) => handleNotificationRadiusChange(parseInt(e.target.value))}
                disabled={saving}
                style={{
                  width: '100%',
                  accentColor: 'var(--color-accent)',
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--color-text)', marginTop: '0.5rem' }}>
                <span>1 km (local)</span>
                <span>500 km (regional)</span>
              </div>
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text)', marginTop: '0.75rem', lineHeight: '1.5' }}>
                You'll receive notifications when public events are created within <strong style={{ color: 'var(--color-accent)' }}>{eventNotificationRadius} km</strong> of your recent check-in locations.
              </p>
            </div>
          )}

          {saving && (
            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: 'var(--color-accent)', textAlign: 'center' }}>
              Saving...
            </p>
          )}
        </div>

        {/* Account Information (read-only username) */}
        <div className="card" style={{ marginTop: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', marginBottom: '0.5rem', fontWeight: '600' }}>
            Account Information
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem' }}>
            <div>
              <span style={{ color: '#999' }}>Username:</span>{' '}
              <span style={{ fontWeight: '500' }}>{currentUser?.username}</span>
            </div>
            <div>
              <span style={{ color: '#999' }}>Smoker Type:</span>{' '}
              <span style={{ fontWeight: '500', textTransform: 'capitalize' }}>{smokerType}</span>
            </div>
            {pipeShapes && pipeShapes.length > 0 && (
              <div>
                <span style={{ color: '#999' }}>Favorite Pipe Shapes:</span>{' '}
                <span style={{ fontWeight: '500' }}>{pipeShapes.join(', ')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Future Settings Placeholder */}
        <div style={{ marginTop: '2rem' }}>
          <div className="card" style={{ padding: '1rem' }}>
            <h3 style={{ fontSize: '1.125rem', marginBottom: '0.5rem', fontWeight: '600', color: '#d3de5e' }}>Privacy & Data</h3>
            <div style={{ fontSize: '0.9rem', color: 'var(--color-text)', lineHeight: 1.6 }}>
              <p style={{ marginBottom: '0.5rem' }}>Your privacy matters.</p>
              <p style={{ marginBottom: '0.5rem' }}>Ember only collects the information needed for the app to function — such as your email address (for sign-in and authentication) and any optional profile details you choose to add.</p>
              <p style={{ marginBottom: '0.5rem' }}>We don’t sell, share, or use your data for advertising or marketing.</p>
              <p style={{ marginBottom: '0.5rem' }}>All data is securely stored and managed by Supabase, our trusted authentication and database provider. Supabase uses industry-standard encryption and security practices.</p>
              <p style={{ marginBottom: '0.5rem' }}>You can delete your account and all associated data at any time by contacting the developer or requesting deletion within the app (coming soon).</p>
              <p style={{ margin: '0.75rem 0 0.35rem', color: '#d3de5e', fontWeight: 600 }}>Summary:</p>
              <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                <li style={{ marginBottom: '0.25rem' }}>Only minimal data (email + profile info) is collected.</li>
                <li style={{ marginBottom: '0.25rem' }}>No tracking, ads, or analytics are used.</li>
                <li style={{ marginBottom: '0.25rem' }}>Your information is never shared or sold.</li>
                <li style={{ marginBottom: '0.25rem' }}>You can delete your data whenever you wish.</li>
              </ul>
              <p style={{ marginTop: '0.75rem' }}>If you have any questions or want your account removed, contact us directly at <a href="mailto:virgils.cain@gmail.com" style={{ color: 'var(--color-accent)' }}>virgils.cain@gmail.com</a>.</p>
            </div>
          </div>
        </div>
      </div>

      <BottomNav />

      {/* Consent modal removed with location sharing feature */}
    </div>
  );
}

